'use client';

import { useTranslations } from 'next-intl';
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';

import { IMAGE_UPLOAD_ACCEPT_ATTR } from '@/components/images/validation';
import { Icon } from '@/components/ui/icon';
import { Upload01Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';

type Props = {
  onFileSelected: (file: File) => void;
  primaryText?: string;
  hintText?: string;
  aspectRatio?: 'square' | 'none';
  errorMessage?: string | null;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
};

export function ImageUploadDropzone({
  onFileSelected,
  primaryText,
  hintText,
  aspectRatio = 'square',
  errorMessage,
  className,
  disabled = false,
  'data-testid': testId = 'image-upload-dropzone',
}: Props): React.ReactNode {
  const tImageUpload = useTranslations('image_upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const primary = primaryText ?? tImageUpload('dropzone_text');
  const hint = hintText ?? tImageUpload('upload_hint');

  function handleClick(): void {
    if (disabled) return;
    fileInputRef.current?.click();
  }

  function handleDrop(e: DragEvent<HTMLButtonElement>): void {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file !== undefined) onFileSelected(file);
  }

  function handleDragEnter(e: DragEvent<HTMLButtonElement>): void {
    e.preventDefault();
    if (disabled) return;
    setDragOver(true);
  }

  function handleDragOver(e: DragEvent<HTMLButtonElement>): void {
    e.preventDefault();
    if (disabled) return;
    setDragOver(true);
  }

  function handleDragLeave(): void {
    setDragOver(false);
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <button
        type="button"
        data-testid={testId}
        data-drag-active={dragOver ? 'true' : 'false'}
        disabled={disabled}
        aria-label={primary}
        className={cn(
          'flex w-full min-w-0 cursor-pointer flex-col items-center justify-center gap-3 rounded-control border-2 border-dashed px-6 py-8 text-center transition-colors',
          aspectRatio === 'square' && 'aspect-square',
          dragOver
            ? 'border-border-primary bg-foreground-primary/5'
            : 'border-border-primary hover:border-border-primary',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Icon icon={Upload01Icon} className="size-6 shrink-0 text-foreground-secondary" />
        <p className="text-body text-foreground-secondary">{primary}</p>
        <p className="text-caption text-foreground-tertiary">{hint}</p>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_UPLOAD_ACCEPT_ATTR}
        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
          const file = e.target.files?.[0];
          if (file !== undefined) onFileSelected(file);
          if (fileInputRef.current !== null) fileInputRef.current.value = '';
        }}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="image-upload-file-input"
      />

      {errorMessage !== null && errorMessage !== undefined && (
        <p role="alert" className="text-caption text-feedback-error-content">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
